import React, { ReactText, useState } from "react"
import ReactModal from "react-modal"
import { useMediaQuery } from "react-responsive"
import tw, { styled, css } from "twin.macro"

import SlideSection from "../components/slideSection"
import NextPageButton from "../components/nextPageButton"
import { modalStyles } from "../utils/modalStyles"
import LearnDetail from "../components/learnDetail"
import CloseIcon from "../svgs/closeIcon"
import Icon4 from "../icons/icon4"
import Icon5 from "../icons/icon5"
import Icon6 from "../icons/icon6"
import Container from "../components/container"
import { SlideProps } from "."
import NextPageContainer from "../components/nextPageContainer"

const Features = styled.div`
  ${tw`flex flex-col md:flex-row justify-center items-center`}
`

const Feature = styled.div`
  max-width: 20.5rem;
  padding: 0 2.1875rem;
  ${tw`flex flex-col items-center text-center md:w-1/3 mb-16 md:mb-0`}
`

const FeatureName = styled.h1`
font-family: Interstate; font-size: 26px; line-height: 28.6px; letter-spacing: 0.41px; font-weight: 300;
${tw`text-center mb-6`}
`

const FeatureDescription = styled.p`
  font-family: Interstate; text-align: center; font-size: 16px; line-height: 26px; font-weight: 300;
  ${tw`text-center mb-6 tracking-wide whitespace-pre-line`}
`

const LearnMoreLink = styled.a`
padding: 12.5px; background-color: #FFFFFF; color: #0063A6; font-size: 14px; font-family: Interstate; font-weight: 700; line-height: 16.8px; letter-spacing: 2px; 
${tw`rounded tracking-wide uppercase hidden md:inline hover:bg-white hover:text-black focus:outline-none text-center block`}
`

interface FeatureData {
  icon: React.FC
  title: ReactText
  description: ReactText
  details?: FeatureDetails[]
}

interface FeatureDetails {
  title?: string
  text: string[]
}

const features: FeatureData[] = [
  {
    icon: Icon4,
    title:
      "Get NZQA recognised, for free",
      description: "Put your passion to work and add valuable knowledge to your CV",
    details: [
      {
        title: "What's a MITO Micro?",
        text: [
          "It’s a bite-sized online course that recognises achievement of specific foundation knowledge. The Introduction to Commercial Road Transport Micro-credential is a great way to gain the foundation knowledge required for an effective introduction to the commercial road transport industry or explore a potential career pathway.",
        ],
      },
      {
        title: "Why should I do it now?",
        text: [
          "This MITO Micro® is free until 31 December 2021. So, whether you’re just starting out or considering switching careers, there’s never been a better time to put yourself in the driver’s seat and get trucking! <br/> <br/>N.B. This course has been designed for people who are no longer at secondary school. If you’re in Year 11-13 at school, then check out <a href='https://www.mito.org.nz/schools/shiftup/' target='_blank'>ShiftUp®</a>."
        ],
      },
    ],
  },
  {
    icon: Icon5,
    title: "Learn the basics in your own time",
    description: "Work at your own speed with interactive online learning",
    details: [
      {
        title: "How does it work?",
        text: [
          "This is an introductory course with Level 2 and 3 unit standards taken from a full NZQA-approved commercial road transport programme, covering the foundation knowledge that will underpin any future learning. It is completed online with videos, interactive modules and theory assessments accessed via any device, so you can work wherever you have access to the internet. Because there’s no practical training involved, you don’t need to be employed in the industry to complete this course.",
        ],
      },
      {
        title: "How long will it take?",
        text: [
          "That depends on you. It can take up to four months (at a rate of 10 hours per week). If you have more time to burn you can get moving faster, but it must be completed within six months of your enrolment.",
        ],
      },
    ],
  },
  {
    icon: Icon6,
    title:
    "Get noticed, get ahead",
    description:
      "Grow knowledge that could help you get a full traineeship",
    details: [
      {
        title: "What’s in it for me?",
        text: [
          "Having the Introduction to Commercial Road Transport Micro-credential on your CV demonstrates a sound knowledge of the basics. This can help you get the attention of employers and could lead to a rewarding career in the industry.",
        ],
      },
      {
        title: "What else can I do to get ahead?",
        text: [
          "MITO is the industry training organisation for the commercial road transport industry, facilitating training across New Zealand. On the MITO Job Hub you can look for current job vacancies. Plus, you can create a free jobseeker profile and promote yourself to employers who may have current job opportunities.",
          'Visit <a style="text-decoration: underline;" href="https://www.mito.org.nz/jobhub" target=_blank>mito.nz/jobhub</a> to create your online profile.',
        ],
      },
    ],
  },
]

const FeaturesSlide: React.SFC<SlideProps> = ({
  slideVisible,
  nextButtonClicked,
}) => {
  const [featureIndex, setFeatureIndex] = useState<number>(-1)

  const isSmall = useMediaQuery({ query: "(max-width: 767px)" })

  return (
    <SlideSection autoHeightResponsive>
          <div
            css={[
              tw`transition duration-1000 ease-out text-center`,
              !slideVisible && tw`opacity-0 transform -translate-y-8`,
            ]}
          >
          <div css={[tw`flex flex-col justify-center items-center text-center mb-16 w-1/3 ml-auto mr-auto mt-20`]}>
            <tspan css={[
              css`font-family:Interstate; font-weight: 700; font-size:60px; line-height: 55.8px; text-align: center; letter-spacing:0.117;
              @media (max-width: 768px) {
                font-size: 45px; 
                line-height: 48px;
                width: 76vw;
              }
              `
            ]}>
                {"Why a Micro?"}
              </tspan>
            </div>
          </div>
      <div css={[tw`flex flex-col justify-center items-center`]}>
        <Container>
          <Features>
            {features.map((feature, index) => {
              const delay = `transition-delay: ${
                slideVisible ? 250 + index * 175 : 0
              }ms`
              return (
                <Feature key={index}>
                  {/* Fade + translate the icon in */}
                  <div
                    css={[
                      css`
                      @media (max-width: 768px) {
                        width: 45vw;
                        margin-bottom: 0.5rem;
                      }
                        ${delay}
                      `,
                      tw`transition duration-1000 ease-out`,
                      !slideVisible && tw`opacity-0 transform -translate-y-8`,
                    ]}
                  >
                    <feature.icon />
                  </div>
                  {/* Fade the text in */}
                  <div
                    css={[
                      css`
                        ${delay}
                      `,
                      tw`transition duration-1000 ease-out`,
                      !slideVisible && tw`opacity-0`,
                    ]}
                  >
                    <FeatureName>{feature.title}</FeatureName>
                    <FeatureDescription>
                      {feature.description}
                    </FeatureDescription>
                    {/* Show learn more if it has more  */}
                    {feature.details?.length ?? 0 > 0 ? (
                      <div css={[tw`text-center`]}>
                        <LearnMoreLink
                          href="#"
                          onClick={() => setFeatureIndex(index)}
                        >
                          Learn more
                        </LearnMoreLink>
                      </div>
                    ) : undefined}
                  </div>
                </Feature>
              )
            })}
          </Features>
        </Container>

        <NextPageContainer>
          <NextPageButton
            onClick={nextButtonClicked}
            label=""
          />
        </NextPageContainer>
      </div>
      {/* When we select 'show more' display it in a modal */}
      <ReactModal
        style={modalStyles(isSmall)}
        isOpen={featureIndex >= 0}
        onRequestClose={() => setFeatureIndex(-1)}
      >
        {featureIndex >= 0 && features[featureIndex]?.details && (
          <LearnDetail items={features[featureIndex].details!} />
        )}
        <div css={[tw`absolute top-0 right-0`]}>
          <button
            css={[tw`p-4 focus:outline-none`]}
            onClick={() => setFeatureIndex(-1)}
          >
            <CloseIcon />
          </button>
        </div>
      </ReactModal>
    </SlideSection>
  )
}

export default FeaturesSlide
